/**
 * Phishing Campaign Detail Page
 * GitHub Issue #285
 *
 * View detailed results and statistics for a phishing campaign.
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  api,
  PhishingCampaign,
  PhishingResult,
  PhishingResultOutcome,
  PhishingCampaignStatus,
} from '../lib/api'
import { useToast } from '../components/Toast'
import { useConfirm } from '../components/ConfirmModal'
import {
  ArrowLeft,
  Play,
  Pause,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Mail,
  MousePointerClick,
  Eye,
  X,
  Download,
  Search,
  Filter,
  Users,
  Shield,
  Lock,
  Flag,
  Calendar,
  TrendingUp,
  ExternalLink,
} from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
} from 'recharts'

const statusColors: Record<PhishingCampaignStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  scheduled: 'bg-purple-100 text-purple-700',
  sending: 'bg-orange-100 text-orange-700',
  active: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
}

const outcomeColors: Record<PhishingResultOutcome, string> = {
  pending: 'bg-gray-100 text-gray-700',
  safe: 'bg-green-100 text-green-700',
  clicked: 'bg-orange-100 text-orange-700',
  compromised: 'bg-red-100 text-red-700',
  reported: 'bg-blue-100 text-blue-700',
}

const outcomeIcons: Record<PhishingResultOutcome, any> = {
  pending: Clock,
  safe: Shield,
  clicked: MousePointerClick,
  compromised: Lock,
  reported: Flag,
}

const CHART_COLORS = ['#22c55e', '#3b82f6', '#f97316', '#ef4444', '#6b7280']

export default function PhishingCampaignDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const toast = useToast()
  const confirmDialog = useConfirm()

  // State
  const [loading, setLoading] = useState(true)
  const [campaign, setCampaign] = useState<PhishingCampaign | null>(null)
  const [results, setResults] = useState<PhishingResult[]>([])
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0 })
  const [stats, setStats] = useState({
    total: 0,
    sent: 0,
    opened: 0,
    clicked: 0,
    compromised: 0,
    reported: 0,
  })

  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [outcomeFilter, setOutcomeFilter] = useState<PhishingResultOutcome | ''>('')

  // Fetch campaign
  const fetchCampaign = useCallback(async () => {
    if (!id) return
    try {
      const response = await api.getPhishingCampaign(id)
      if (response.success) {
        setCampaign(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch campaign:', error)
      toast.error('Failed to load campaign')
    }
  }, [id, toast])

  // Fetch results
  const fetchResults = useCallback(async () => {
    if (!id) return
    try {
      const params: any = { limit: pagination.limit, offset: pagination.offset }
      if (searchTerm) params.search = searchTerm
      if (outcomeFilter) params.outcome = outcomeFilter

      const response = await api.getPhishingCampaignResults(id, params)
      if (response.success) {
        setResults(response.data)
        setPagination(response.pagination)
        setStats(response.stats)
      }
    } catch (error) {
      console.error('Failed to fetch results:', error)
    }
  }, [id, searchTerm, outcomeFilter, pagination.limit, pagination.offset])

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      await fetchCampaign()
      setLoading(false)
    }
    loadData()
  }, [fetchCampaign])

  useEffect(() => {
    if (campaign) {
      fetchResults()
    }
  }, [campaign, fetchResults])

  // Campaign actions
  const handleApproveCampaign = async () => {
    if (!campaign) return
    const confirmed = await confirmDialog({
      title: 'Approve Campaign',
      message: `Are you sure you want to approve "${campaign.name}" for sending?`,
      confirmText: 'Approve',
      type: 'warning',
    })
    if (confirmed) {
      try {
        const response = await api.approvePhishingCampaign(campaign.id)
        if (response.success) {
          toast.success('Campaign approved')
          fetchCampaign()
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to approve campaign')
      }
    }
  }

  const handleStartCampaign = async () => {
    if (!campaign) return
    const confirmed = await confirmDialog({
      title: 'Start Campaign',
      message: `Are you sure you want to start sending "${campaign.name}"? This will send phishing emails to all targeted users.`,
      confirmText: 'Start Sending',
      type: 'danger',
    })
    if (confirmed) {
      try {
        const response = await api.startPhishingCampaign(campaign.id)
        if (response.success) {
          toast.success(`Campaign started! ${response.emails_queued} emails queued.`)
          fetchCampaign()
          fetchResults()
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to start campaign')
      }
    }
  }

  const handleCancelCampaign = async () => {
    if (!campaign) return
    const confirmed = await confirmDialog({
      title: 'Cancel Campaign',
      message: `Are you sure you want to cancel "${campaign.name}"?`,
      confirmText: 'Cancel Campaign',
      type: 'danger',
    })
    if (confirmed) {
      try {
        const response = await api.cancelPhishingCampaign(campaign.id)
        if (response.success) {
          toast.success('Campaign cancelled')
          fetchCampaign()
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to cancel campaign')
      }
    }
  }

  // Export results
  const handleExport = async () => {
    if (!campaign) return
    try {
      const blob = await api.exportPhishingCampaignResults(campaign.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `phishing-results-${campaign.name.replace(/\s+/g, '-')}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast.success('Results exported')
    } catch (error: any) {
      toast.error(error.message || 'Failed to export results')
    }
  }

  // Calculate rates
  const calculateRate = (part: number, total: number) => {
    if (total === 0) return 0
    return (part / total) * 100
  }

  // Prepare chart data
  const getOutcomeChartData = () => {
    return [
      { name: 'Safe', value: stats.total - stats.clicked - stats.compromised - stats.reported, color: CHART_COLORS[0] },
      { name: 'Clicked', value: stats.clicked - stats.compromised, color: CHART_COLORS[2] },
      { name: 'Compromised', value: stats.compromised, color: CHART_COLORS[3] },
      { name: 'Reported', value: stats.reported, color: CHART_COLORS[1] },
    ].filter(d => d.value > 0)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!campaign) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Campaign not found</h2>
        <p className="text-gray-500 mb-4">The phishing campaign you're looking for doesn't exist.</p>
        <Link to="/phishing" className="btn btn-primary">
          Back to Phishing
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/phishing')}
            className="btn btn-secondary"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{campaign.name}</h1>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusColors[campaign.status]}`}>
                {campaign.status}
              </span>
              {campaign.requires_approval && !campaign.approved_at && campaign.status === 'draft' && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                  Awaiting Approval
                </span>
              )}
            </div>
            {campaign.description && (
              <p className="text-gray-500 mt-1">{campaign.description}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {campaign.status === 'draft' && campaign.requires_approval && !campaign.approved_at && (
            <button
              onClick={handleApproveCampaign}
              className="btn btn-secondary text-green-600"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Approve
            </button>
          )}
          {campaign.status === 'draft' && (!campaign.requires_approval || campaign.approved_at) && (
            <button
              onClick={handleStartCampaign}
              className="btn btn-primary"
            >
              <Play className="h-4 w-4 mr-2" />
              Start Sending
            </button>
          )}
          {(campaign.status === 'active' || campaign.status === 'sending') && (
            <button
              onClick={handleCancelCampaign}
              className="btn btn-secondary text-red-600"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </button>
          )}
          {stats.sent > 0 && (
            <button
              onClick={handleExport}
              className="btn btn-secondary"
            >
              <Download className="h-4 w-4 mr-2" />
              Export Results
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-500 text-sm">Targets</span>
            <Users className="h-5 w-5 text-gray-400" />
          </div>
          <p className="text-2xl font-bold mt-2">{campaign.total_targets}</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-500 text-sm">Sent</span>
            <Mail className="h-5 w-5 text-blue-500" />
          </div>
          <p className="text-2xl font-bold mt-2">{stats.sent}</p>
          <p className="text-xs text-gray-400">{calculateRate(stats.sent, campaign.total_targets).toFixed(1)}%</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-500 text-sm">Opened</span>
            <Eye className="h-5 w-5 text-purple-500" />
          </div>
          <p className="text-2xl font-bold mt-2">{stats.opened}</p>
          <p className="text-xs text-gray-400">{calculateRate(stats.opened, stats.sent).toFixed(1)}%</p>
        </div>
        <div className="bg-orange-50 rounded-lg border border-orange-200 p-4">
          <div className="flex items-center justify-between">
            <span className="text-orange-700 text-sm">Clicked</span>
            <MousePointerClick className="h-5 w-5 text-orange-500" />
          </div>
          <p className="text-2xl font-bold text-orange-700 mt-2">{stats.clicked}</p>
          <p className="text-xs text-orange-500">{calculateRate(stats.clicked, stats.sent).toFixed(1)}%</p>
        </div>
        <div className="bg-red-50 rounded-lg border border-red-200 p-4">
          <div className="flex items-center justify-between">
            <span className="text-red-700 text-sm">Compromised</span>
            <Lock className="h-5 w-5 text-red-500" />
          </div>
          <p className="text-2xl font-bold text-red-700 mt-2">{stats.compromised}</p>
          <p className="text-xs text-red-500">{calculateRate(stats.compromised, stats.sent).toFixed(1)}%</p>
        </div>
        <div className="bg-green-50 rounded-lg border border-green-200 p-4">
          <div className="flex items-center justify-between">
            <span className="text-green-700 text-sm">Reported</span>
            <Flag className="h-5 w-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-green-700 mt-2">{stats.reported}</p>
          <p className="text-xs text-green-500">{calculateRate(stats.reported, stats.sent).toFixed(1)}%</p>
        </div>
      </div>

      {/* Charts */}
      {stats.sent > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Outcome Distribution */}
          <div className="bg-white rounded-lg border p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Outcome Distribution</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={getOutcomeChartData()}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {getOutcomeChartData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-4">
              {getOutcomeChartData().map((entry) => (
                <div key={entry.name} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-sm text-gray-600">{entry.name}: {entry.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Campaign Info */}
          <div className="bg-white rounded-lg border p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Campaign Details</h3>
            <dl className="space-y-4">
              <div className="flex justify-between">
                <dt className="text-gray-500">Template</dt>
                <dd className="font-medium text-gray-900">
                  {campaign.template_name || '-'}
                  {campaign.template_id && (
                    <Link
                      to={`/phishing/templates/${campaign.template_id}`}
                      className="ml-2 text-primary-600 hover:text-primary-700"
                    >
                      <ExternalLink className="h-3 w-3 inline" />
                    </Link>
                  )}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Sender</dt>
                <dd className="font-medium text-gray-900">
                  {campaign.sender_name ? `${campaign.sender_name} <${campaign.sender_email}>` : '-'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Target Type</dt>
                <dd className="font-medium text-gray-900">{campaign.target_type.replace(/_/g, ' ')}</dd>
              </div>
              {campaign.scheduled_at && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Scheduled</dt>
                  <dd className="font-medium text-gray-900">
                    {new Date(campaign.scheduled_at).toLocaleString()}
                  </dd>
                </div>
              )}
              {campaign.started_at && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Started</dt>
                  <dd className="font-medium text-gray-900">
                    {new Date(campaign.started_at).toLocaleString()}
                  </dd>
                </div>
              )}
              {campaign.completed_at && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Completed</dt>
                  <dd className="font-medium text-gray-900">
                    {new Date(campaign.completed_at).toLocaleString()}
                  </dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-gray-500">Created by</dt>
                <dd className="font-medium text-gray-900">{campaign.created_by_username || '-'}</dd>
              </div>
              {campaign.approved_by_username && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Approved by</dt>
                  <dd className="font-medium text-gray-900">{campaign.approved_by_username}</dd>
                </div>
              )}
            </dl>

            {/* Tracking Options */}
            <div className="mt-6 pt-4 border-t">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Tracking Options</h4>
              <div className="flex flex-wrap gap-2">
                {campaign.track_opens && (
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">
                    Track Opens
                  </span>
                )}
                {campaign.track_clicks && (
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">
                    Track Clicks
                  </span>
                )}
                {campaign.capture_credentials && (
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">
                    Capture Credentials
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results Table */}
      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b flex flex-col md:flex-row md:items-center justify-between gap-4">
          <h3 className="font-semibold text-gray-900">Individual Results</h3>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10 w-64"
              />
            </div>
            <select
              value={outcomeFilter}
              onChange={(e) => setOutcomeFilter(e.target.value as PhishingResultOutcome | '')}
              className="input"
            >
              <option value="">All Outcomes</option>
              <option value="pending">Pending</option>
              <option value="safe">Safe</option>
              <option value="clicked">Clicked</option>
              <option value="compromised">Compromised</option>
              <option value="reported">Reported</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Outcome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Opened</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clicked</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reported</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {results.map((result) => {
                const OutcomeIcon = outcomeIcons[result.outcome]
                return (
                  <tr key={result.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="font-medium text-gray-900">{result.user_username || result.email}</p>
                        <p className="text-sm text-gray-500">{result.email}</p>
                        {result.user_department && (
                          <p className="text-xs text-gray-400">{result.user_department}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${outcomeColors[result.outcome]}`}>
                        <OutcomeIcon className="h-3 w-3" />
                        {result.outcome}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {result.sent_at ? new Date(result.sent_at).toLocaleString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {result.opened_at ? new Date(result.opened_at).toLocaleString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {result.clicked_at ? (
                        <span className="text-orange-600 font-medium">
                          {new Date(result.clicked_at).toLocaleString()}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {result.submitted_at ? (
                        <span className="text-red-600 font-medium">
                          {new Date(result.submitted_at).toLocaleString()}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {result.reported_at ? (
                        <span className="text-green-600 font-medium">
                          {new Date(result.reported_at).toLocaleString()}
                        </span>
                      ) : '-'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          {results.length === 0 && (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                {campaign.status === 'draft'
                  ? 'Results will appear once the campaign is started'
                  : 'No results match your filters'
                }
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.total > pagination.limit && (
          <div className="px-6 py-4 border-t flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {pagination.offset + 1} to {Math.min(pagination.offset + results.length, pagination.total)} of {pagination.total}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(p => ({ ...p, offset: Math.max(0, p.offset - p.limit) }))}
                disabled={pagination.offset === 0}
                className="btn btn-sm btn-secondary disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(p => ({ ...p, offset: p.offset + p.limit }))}
                disabled={pagination.offset + results.length >= pagination.total}
                className="btn btn-sm btn-secondary disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
